import { Component, Inject, Input, EventEmitter, Output, ViewChild } from '@angular/core';

import { BehaviorSubject, Subject, combineLatest, forkJoin } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';

import { MultiFilterableSelectComponent } from '../../../../shared/filterable-select/components/multi-filterable-select.component';

import { AntenneService } from '../../antenne/services/antenne.service';
import { ParcService } from '../services/parc.service';
import { ParcWithGroupementDTO } from '../entities/parc-with-groupement-dto.entity';

@Component({
	selector: 'parc-filterable-selector',
	templateUrl: 'parc-filterable-selector.html',
})
export class ParcFilterableSelectorComponent {
	parcs: ParcWithGroupementDTO[] = [];
	selectedParc: ParcWithGroupementDTO | undefined = undefined;

	getParcLabel = (parc: ParcWithGroupementDTO) => parc.code + ' (' + (parc.description ? parc.description : '') + ')';
	
	agenceIdSubject = new BehaviorSubject<number | null | undefined>(undefined);
	antenneIdSubject = new BehaviorSubject<number | null | undefined>(undefined);
	groupementIdSubject = new BehaviorSubject<number | null | undefined>(undefined);
	selectedParcIdSubject = new Subject<number | null | undefined>();

	constructor( //
		@Inject(AntenneService) antenneService: AntenneService, //
		@Inject(ParcService) parcService: ParcService, //
	) {
		// A la reception d'un nouveau numero d'antenne, recuperer les parcs rattachees
		let parcs$ = combineLatest([this.agenceIdSubject, this.antenneIdSubject, this.groupementIdSubject]).pipe(
			switchMap(
				([agenceId, antenneId, groupementId]) =>  {
					if (groupementId != null && groupementId != undefined) {
						return parcService.getParcTLRByGroupement(groupementId);
					} else if (antenneId != null && antenneId != undefined) {
						return parcService.getParcTLRByAntenne(antenneId)
					} else if (agenceId != null && agenceId != undefined) {
						return antenneService.getAntenneTLRByAgence(agenceId).pipe(
							switchMap(
								(antennes) => {
									let parcsLists$ = antennes.map(
										(antenne) => parcService.getParcTLRByAntenne(antenne.id)
									);
									
									return forkJoin(parcsLists$);
								}
							),
							map(
								(parcsLists) => {
									let parcs: ParcWithGroupementDTO[] = [];
									parcsLists.forEach(
										(parcList) => parcList.forEach(
											(parc) => parcs.push(parc)
										)
									);
									parcs.sort(
										(p1, p2) => p1.code.localeCompare(p2.code)
									);
									return parcs;
								}
							)
						);
					} else {
						return parcService.getParcTLRList();
					}
				}
			)
		);
		
		// Une fois les parcs chargees les transmettre au sous-composant
		parcs$.subscribe(
			(parcs : ParcWithGroupementDTO[]) => {
				if (parcs) {
					parcs = parcs.filter( p => p.actif );
					parcs.sort( (p1, p2) => p1.code.localeCompare(p2.code) );
					this.parcs = parcs;
				}
			}
		);

		// Definit comment combiner les donnees sur l'ensemble des parcs connues avec un id d'parc pour fixer l'parc actuellement selectionnee 
		combineLatest([parcs$, this.selectedParcIdSubject]).subscribe(
			([parcs, parcId]) => {
				let newSelectedParc: ParcWithGroupementDTO | undefined = undefined; 
				if (parcId) {
					let parc = parcs.find(parc => parc.id === parcId);
					newSelectedParc = parc;
				}
				
				if (newSelectedParc != this.selectedParc) {
					this.selectedParc = newSelectedParc;
					this.onSelectedParcChange(this.selectedParc);
				}
			}
		);
	}

	@ViewChild(MultiFilterableSelectComponent) input!: MultiFilterableSelectComponent;

	@Input() id!: string;
	@Input() disabled = false;
	
	// Classes pour l'affichege
	public _mandatory: boolean = false;
	@Input() set mandatory(value: boolean | undefined) {
		if (value) {
			this._mandatory = value;
		}
	}
	get mandatory(): boolean | undefined {
		return this._mandatory;
	}
	public _labelClass: string = 'col-md-1';
	@Input() set labelClass(value: string | undefined) {
		if (value) {
			this._labelClass = value;
		}	
	}
	get labelClass(): string | undefined {
		return this._labelClass;
	}
	
	public _fieldClass: string = 'col-md-7';
	@Input() set fieldClass(value: string | undefined) {
		if (value) {
			this._fieldClass = value;
		}
	}
	get fieldClass(): string | undefined {
		return this._fieldClass;
	}
	
	@Input() set agenceId(id: number | null | undefined) {
		this.agenceIdSubject.next(id);
	}
	
	@Input() set antenneId(id: number | null | undefined) {
		this.antenneIdSubject.next(id);
	}
	
	@Input() set groupementId(id: number | null | undefined) {
		this.groupementIdSubject.next(id);
	}
	
	private _selectedParcId: number | null | undefined = null;
	@Input() set selectedParcId(id: number | null | undefined) {
		this._selectedParcId = id;
		this.selectedParcIdSubject.next(id);
	}
	get selectedParcId(): number | null | undefined {
		return this._selectedParcId;
	}
	
	@Output() selectedParcIdChange = new EventEmitter<number | null>();
	
	@Output() selectedParcChange = new EventEmitter<ParcWithGroupementDTO | null>();
	
	onSelectedParcChange(parc: ParcWithGroupementDTO | null | undefined) {
		let id = parc ? parc.id : null;
		this._selectedParcId = id;
		this.selectedParcIdChange.emit(id);
		this.selectedParcChange.emit(parc);
	}
}